import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { DrawerLink } from '../../app/DrawerLink';
import { subscribeToStation } from '../../lib/realtime';

import { EmojiButton } from '../../components/ui/EmojiPicker';
import { emojiOnly, emojiScale, useEmojiComposer } from '../../lib/emoji';
import { useSession } from '../../stores/session';
import { rooms } from './api';
import { REACTION_META } from './reactions';

/**
 * How long a reaction stays written down.
 *
 * A minute, which is roughly how long it is worth a host knowing about. Longer
 * and these stop being "the room just did something" and start being a
 * transcript of things nobody said.
 */
const NOTICE_MS = 60_000;

/** And how many at once, so a full room is a hint rather than a wall. */
const NOTICE_MAX = 4;

/**
 * Room chat.
 *
 * ## Read by anybody, written by an account
 *
 * Same shape as the rest of the site: reading is the advertisement, writing is
 * the reason to have an account. A signed-out visitor sees the conversation and
 * a prompt to join, not an input that fails when they use it.
 *
 * ## It follows the socket, and refetches on its own anyway
 *
 * It subscribes for `station.chat` itself, which is the fast path; the slow
 * poll underneath covers a dropped connection - §8.2's rule that the socket is
 * a delivery mechanism, applied to the one part of a room where nobody would
 * notice they had stopped receiving anything.
 *
 * It used to say `useRoom` did the invalidating. It did not: `useRoom`
 * invalidates `['room', username]` and this query is `['room', 'chat',
 * username]`, which is not a prefix of it - so every chat event landed on the
 * wrong key and the ten-second poll was doing all the work, always, however
 * healthy the socket was. The component that owns the data subscribes for it,
 * the same as the crowd and the queue widget.
 */
export function ChatPanel({
  username,
  stationId,
  divided = true,
  canModerate = false,
  canChat = true,
  policy = 'open',
}: {
  username: string;
  /** The room's own id, to subscribe for its messages. */
  stationId: number;
  /**
   * Whether to offer removal on each message.
   *
   * Passed in rather than worked out here, because the answer differs by
   * screen: the console knows it is the host's own room, a profile does not
   * without asking. The server checks regardless - this only decides whether
   * the control is drawn.
   */
  canModerate?: boolean;
  /**
   * Whether this reader may post at all.
   *
   * The server's answer rather than a guess: only it knows whether somebody
   * follows the host. Defaulted to true so a caller that has not been given it
   * still shows the composer and lets the POST come back with the reason -
   * which is the better of the two failures.
   */
  canChat?: boolean;
  /** The room's chat setting, so a closed composer can say why. */
  policy?: 'open' | 'followers' | 'closed';
  /**
   * Whether to rule off from whatever is above.
   *
   * True in the console, where this sits under the session figures. False on a
   * profile, where it is the whole column and the border would read as a stray
   * line across the top of it.
   */
  divided?: boolean;
}) {
  const user = useSession((state) => state.user);
  const queryClient = useQueryClient();
  const [body, setBody] = useState('');

  /** Who has reacted lately, and when. See the note where these are rendered. */
  const [reactions, setReactions] = useState<Record<string, { reaction: string; at: number }>>({});

  /*
   * A clock, so a notice disappears on its own rather than when the next
   * message happens to arrive. One tick a second is enough for a minute-long
   * life and costs nothing next to what a live room is already doing.
   */
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);

    return () => clearInterval(timer);
  }, []);
  const emoji = useEmojiComposer(body, setBody);
  const scroller = useRef<HTMLDivElement>(null);

  const { data } = useQuery({
    queryKey: ['room', 'chat', username],
    queryFn: () => rooms.chat(username),
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
  });

  /*
   * Its own subscription, keyed to its own query. See the note on the
   * component: the event was landing on `useRoom`'s key and doing nothing here.
   */
  useEffect(
    () =>
      subscribeToStation(stationId, (event, payload) => {
        if (event === 'station.chat') {
          void queryClient.invalidateQueries({ queryKey: ['room', 'chat', username] });

          return;
        }

        /*
         * Somebody in the crowd did something.
         *
         * The host is the one person who needs to know and the one person who
         * cannot see it: they are on the console watching this panel, not on
         * their own profile watching the stage.
         */
        if (event !== 'station.react') return;

        const { username: who, reaction } = (payload ?? {}) as {
          username?: string;
          reaction?: string;
        };

        if (!who || !reaction || REACTION_META[reaction] === undefined) return;

        setReactions((current) => ({ ...current, [who]: { reaction, at: Date.now() } }));
      }),
    [stationId, username, queryClient],
  );

  const remove = useMutation({
    mutationFn: (id: number) => rooms.removeMessage(username, id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['room', 'chat', username] }),
  });

  const say = useMutation({
    mutationFn: () => rooms.say(username, body.trim()),
    onSuccess: () => {
      setBody('');
      void queryClient.invalidateQueries({ queryKey: ['room', 'chat', username] });
    },
  });

  const items = data?.items ?? [];

  /*
   * The last few reactions, as stage directions rather than as messages.
   *
   * Held here and never sent anywhere. A reaction is a moment with no row
   * behind it, and writing "cairn clapped" into the transcript would fill the
   * scrollback of a busy room with things nobody said - so these live for a
   * minute and go, which is also what makes them safe to pin at the bottom
   * without getting the order wrong.
   *
   * Keyed by person, so somebody leaning on the clap button is one line that
   * keeps moving rather than forty.
   */
  const recent = Object.entries(reactions)
    .filter(([, { at }]) => now - at < NOTICE_MS)
    .sort(([, a], [, b]) => a.at - b.at)
    .slice(-NOTICE_MAX);

  /*
   * Pinned to the bottom as messages arrive.
   *
   * Only when already near it: yanking somebody who scrolled up to read
   * something back to the newest line is the single most irritating thing a
   * chat panel can do.
   */
  useEffect(() => {
    const element = scroller.current;

    if (!element) return;

    const nearBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 120;

    if (nearBottom) element.scrollTop = element.scrollHeight;
  }, [items.length]);

  const submit = (event: FormEvent) => {
    event.preventDefault();

    if (body.trim() !== '') say.mutate();
  };

  return (
    <section className={`flex min-h-0 flex-1 flex-col gap-3 ${divided ? 'border-t border-line pt-5' : ''}`}>
      {/*
        Padded individually rather than on the section, so the message list's
        scrollbar reaches the edge of the column. Inset it and the bar floats in
        the middle of a margin, which reads as broken.
      */}
      <div className="flex items-center justify-between px-6">
        <span className="meta-caps text-faint">Room chat</span>
        {/* Slow mode is the route's rate limit rather than a column. Said here
            so somebody who hits it knows it was a rule, not a failure. */}
        <span className="font-mono text-[10px] text-ghost">20 a minute</span>
      </div>

      <div
        ref={scroller}
        className="flex min-h-[8rem] flex-1 flex-col gap-3 overflow-y-auto px-6"
        data-chat
      >
        {items.length === 0 && <p className="text-sm text-soft">Nobody has said anything yet.</p>}

        {items.map((message) => {
          /* How many emoji, if that is all this is. Null for ordinary text. */
          const big = message.body === null ? null : emojiOnly(message.body);

          return (
          /*
            The room talking, not a person in it.

            Centred and unattributed so it reads as a stage direction rather
            than as somebody with an opinion, and without a remove control: a
            host deleting the notice that they closed chat would leave the
            transcript saying something untrue.
          */
          message.kind === 'system' ? (
            <p
              key={message.id}
              data-chat-notice
              className="py-1 text-center text-[11px] italic text-faint"
            >
              {message.body}
            </p>
          ) : (
            <div key={message.id} className="group flex flex-col gap-0.5">
              <span className="flex items-center gap-2 font-mono text-[10px] text-accent">
                {message.from ? (
                  <DrawerLink to={`/${message.from.username}`} className="hover:underline">
                    @{message.from.username}
                  </DrawerLink>
                ) : (
                  'someone'
                )}

                {/*
                  Shown on hover rather than always. A row of delete buttons down
                  a live conversation reads as a moderation queue; a host wants to
                  read the room and reach for one when they need it.
                */}
                {canModerate && message.body !== null && (
                  <button
                    type="button"
                    data-remove-message={message.id}
                    aria-label={`Remove message from ${message.from?.username ?? 'someone'}`}
                    title="Remove"
                    disabled={remove.isPending}
                    onClick={() => remove.mutate(message.id)}
                    className="text-ghost opacity-0 transition-opacity hover:text-hot-text focus:opacity-100 group-hover:opacity-100"
                  >
                    remove
                  </button>
                )}
              </span>
              {message.body === null ? (
                <span className="text-[13px] italic text-faint">Message removed.</span>
              ) : (
                /*
                  A message that is nothing but emoji is drawn big.
                
                  It is a gesture rather than a sentence - the same thing the
                  reaction buttons do, typed - and at thirteen pixels a single
                  thumb is easy to miss entirely in a moving room. Capped at ten,
                  because past that it stops being a gesture: see `emojiOnly`.
                */
                <span
                  data-chat-emoji={big ?? undefined}
                  className={
                    big === null
                      ? 'text-[13px] leading-relaxed text-text-2'
                      : emojiScale(big)
                  }
                >
                  {message.body}
                </span>
              )}
            </div>
          )
          );
        })}

        {/*
          What the room just did, under what it just said.

          Styled as the room talking, like the system notices above, because
          that is what it is - nobody typed this. Pinned at the bottom rather
          than interleaved: they are all from the last minute by construction,
          so "just now" is the only place they could go.
        */}
        {recent.map(([who, { reaction }]) => (
          /*
            Drawn as something that just happened, not as a footnote.

            These started as the same faint italic the system notices use, and
            that was wrong for the one thing they are for: a host on the console
            is not reading the panel closely, they are glancing at it between
            other work. So it is a pill in the accent, with the glyph the person
            actually pressed on the front of it - the emoji is the part that is
            legible at a glance, and the words are there for when it is worth
            reading.
          */
          <div key={who} className="flex justify-center py-0.5">
            <span
              data-chat-reaction={who}
              style={{ animation: 'gv-fade-in 200ms ease-out' }}
              className="inline-flex max-w-full items-center gap-2 rounded-[var(--radius-pill)] border border-accent/25 bg-accent/10 px-3 py-1.5"
            >
              <span aria-hidden className="text-[15px] leading-none">
                {REACTION_META[reaction]?.glyph}
              </span>
              <span className="truncate text-[11px] text-text-2">
                <span className="font-medium text-accent">@{who}</span>{' '}
                {REACTION_META[reaction]?.verb}
              </span>
            </span>
          </div>
        ))}
      </div>

      {!user ? (
        <p className="px-6 text-xs text-faint">
          <Link to="/login" className="text-accent hover:underline">
            Sign in
          </Link>{' '}
          to say something.
        </p>
      ) : !canChat ? (
        /*
          Said in place of the composer rather than after a rejected send.

          `ChatAction` refuses either way, so this changes nothing about what is
          allowed - only about when somebody finds out. Learning a room's rule
          by writing a sentence and watching it bounce is the worse of the two.
        */
        <p className="px-6 text-xs text-faint" data-chat-closed={policy}>
          {policy === 'followers' ? (
            <>
              Only followers can post here.{' '}
              <Link to={`/${username}`} className="text-accent hover:underline">
                Follow @{username}
              </Link>{' '}
              to join in.
            </>
          ) : (
            'The host has turned chat off. You can still read along.'
          )}
        </p>
      ) : (
        <form
          onSubmit={submit}
          /*
            `focus-within` on the row, because the field inside it is bare - it has
            no border of its own to change. The whole composer lighting up is
            also the truer thing to show: the emoji button and send are part of
            it, and focus is really on the row.
          */
          className="mx-6 flex items-center gap-2 rounded-[var(--radius-pill)] border border-line bg-raised px-3 py-1.5 transition-colors focus-within:border-line-lit"
        >
          <input
            ref={emoji.ref as React.RefObject<HTMLInputElement>}
            value={body}
            maxLength={500}
            data-chat-input
            placeholder="say something to the room"
            onChange={(event) => setBody(event.target.value)}
            className="min-w-0 flex-1 bg-transparent text-[13px] text-ink outline-none placeholder:text-faint"
          />
          <EmojiButton onSelect={emoji.insert} align="right" />
          <button
            type="submit"
            disabled={body.trim() === '' || say.isPending}
            className="shrink-0 font-mono text-[10px] text-accent disabled:text-ghost"
          >
            send
          </button>
        </form>
      )}
    </section>
  );
}
